// S7 — Sesión de atribución de landing.
//
// Problema corregido: el `landing_session_id` vivía en localStorage para
// siempre, así que un mismo navegador que volvía días después desde otra
// campaña heredaba la sesión antigua y el nuevo origen no quedaba registrado.
//
// Reglas:
// - Ventana de inactividad: 30 minutos.
// - Mismo fingerprint de atribución dentro de la ventana => misma sesión
//   (navegar `/` -> `/auth` con los mismos parámetros NO rota la sesión).
// - Fingerprint distinto (otra campaña, otro gclid...) => nueva sesión inmediata.
// - Entrada directa (sin parámetros) tras 30 min => nueva sesión.
// - El fingerprint sólo usa parámetros de atribución de la URL; nunca datos de
//   la persona.

export const SESSION_WINDOW_MS = 30 * 60 * 1000;

export const SESSION_STORAGE_KEY = 'landing_session_id';
export const SESSION_STATE_KEY = 'landing_session_state';

/** Parámetros que definen el origen de una entrada. */
export const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'src',
  'ref',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'ttclid',
] as const;

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];

interface SessionState {
  id: string;
  fingerprint: string;
  lastSeen: number;
}

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* localStorage bloqueado: la sesión vive sólo en memoria */
  }
};

/** Huella de atribución de la entrada actual (vacía = entrada directa). */
export const buildAttributionFingerprint = (search: string): string => {
  const params = new URLSearchParams(search ?? '');
  const parts: string[] = [];
  for (const key of ATTRIBUTION_KEYS) {
    const values = params.getAll(key);
    if (values.length === 0) continue;
    const value = values[values.length - 1].trim();
    if (value.length > 0) parts.push(`${key}=${value}`);
  }
  return parts.join('&');
};

const newSessionId = (now: number): string =>
  `session_${now}_${Math.random().toString(36).slice(2, 11)}`;

const readState = (): SessionState | null => {
  const raw = safeGet(SESSION_STATE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SessionState>;
    if (typeof parsed?.id !== 'string' || typeof parsed?.lastSeen !== 'number') return null;
    return {
      id: parsed.id,
      fingerprint: typeof parsed.fingerprint === 'string' ? parsed.fingerprint : '',
      lastSeen: parsed.lastSeen,
    };
  } catch {
    return null;
  }
};

const writeState = (state: SessionState): void => {
  safeSet(SESSION_STATE_KEY, JSON.stringify(state));
  safeSet(SESSION_STORAGE_KEY, state.id);
};

/**
 * Devuelve el id de sesión de atribución vigente, rotándolo cuando cambia el
 * origen o cuando expira la ventana de inactividad. Actualiza `lastSeen`.
 */
export const resolveLandingSessionId = (
  search: string = typeof window !== 'undefined' ? window.location.search : '',
  now: number = Date.now()
): string => {
  const fingerprint = buildAttributionFingerprint(search);
  const previous = readState();

  if (previous) {
    const expired = now - previous.lastSeen > SESSION_WINDOW_MS;
    // Una entrada directa (sin parámetros) no rota la sesión: es navegación interna.
    const changedOrigin = fingerprint.length > 0 && fingerprint !== previous.fingerprint;
    if (!expired && !changedOrigin) {
      const updated: SessionState = { ...previous, lastSeen: now };
      writeState(updated);
      return updated.id;
    }
  }

  const fresh: SessionState = { id: newSessionId(now), fingerprint, lastSeen: now };
  writeState(fresh);
  return fresh.id;
};

/** Sólo para pruebas: limpia el estado persistido. */
export const resetLandingSessionForTests = (): void => {
  try {
    localStorage.removeItem(SESSION_STATE_KEY);
    localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    /* noop */
  }
};
